/**
 * Which threads have a turn in flight, said to the store for Ask at the foot
 * of the page. It is read from each thread's own `Chat` rather than from the
 * surface that shows it, so it outlives the dock or the Coach page: a turn
 * keeps working for a moment after its surface closes (`holdThread`'s let-go),
 * and that moment, the only one when Ask is on screen, is when "Working…"
 * has something to say. The stop that follows is a status change like any
 * other, and clears it.
 */
import { useCoachStore } from './coach-store';

/** The part of the SDK's `Chat` this reads: its status, and word of each change. */
export type StatusSource = {
  readonly status: string;
  readonly '~registerStatusCallback': (onChange: () => void) => () => void;
};

const sources = new Map<string, StatusSource>();
/** Threads whose follow-up after a confirm waits on the outbox before it sends. */
const continuing = new Set<string>();

/** Called each time a thread is found free: the queued question waits on it (`thread-turns`). */
const onFree = new Map<string, () => void>();

/** The threads last said to be working, so a turn that ends is told once. */
const working = new Set<string>();
/** Whether this thread has a turn in flight, or its follow-up after a confirm waiting, at this instant. */
export const isThreadBusy = (id: string): boolean => {
  const status = sources.get(id)?.status;
  return status === 'submitted' || status === 'streaming' || continuing.has(id);
};

/** When OS first started answering each thread: the server stores the person's line before the model runs, so from then it keeps the thread. */
const savedAt = new Map<string, number>();

/** When the server first kept this thread, as this tab saw it, or null while it may not have it. */
export const savedAtOf = (id: string): number | null => savedAt.get(id) ?? null;

/** The server kept this thread: OS started answering, or a call's kept lines joined it. The first time stands. */
export const noteSaved = (id: string): void => {
  if (!savedAt.has(id)) savedAt.set(id, Date.now());
};

const report = (id: string): void => {
  if (sources.get(id)?.status === 'streaming') noteSaved(id);
  const busy = isThreadBusy(id);
  useCoachStore.getState().setThreadBusy(id, busy);
  const ended = working.has(id) && !busy;
  if (busy) working.add(id);
  else working.delete(id);
  // A turn that ends is a thread the server now keeps (or keeps newer), so
  // every list of past conversations reads again. Told here, from the thread's
  // own chat: a new conversation left while OS answered has no surface to say it.
  if (ended) useCoachStore.getState().bump();
  if (!busy) onFree.get(id)?.();
};

/** Run `free` every time the thread is found free of a turn; one per thread, a later call replaces it. */
export const whenFree = (id: string, free: () => void): void => {
  onFree.set(id, free);
};

/** Follow this thread's chat for as long as the tab holds it. */
export const trackBusy = (id: string, chat: StatusSource): void => {
  sources.set(id, chat);
  chat['~registerStatusCallback'](() => report(id));
  report(id);
};

/** The follow-up after a confirm is waiting (the outbox drains first), or no longer is. */
export const setContinuing = (id: string, on: boolean): void => {
  if (on) continuing.add(id);
  else continuing.delete(id);
  report(id);
};
